import { Col } from "@commonlibs";
import { alpha } from "@commonlibs";
import styled, { css } from "styled-components";
import { LoaderPositions, UploadInputDisplayRootProps } from "./UploadInputDisplay.types";

const fileUploadSizeStyles = css(
    () => css`
        &.xs {
            width: 3.75rem;
            min-height: 3.75rem;
        }
        &.sm {
            width: 5.75rem;
            min-height: 5.75rem;
        }
        &.md {
            width: 7.75rem;
            min-height: 7.75rem;
        }
        &.lg {
            width: 13rem;
            min-height: 13rem;
        }
        &.xl {
            width: 15.5rem;
            min-height: 15.5rem;
        }
    `,
);

const LOADER_POSITIONS: LoaderPositions = {
    xs: { x: 2, y: 2 },
    sm: { x: 2, y: 2 },
    md: { x: 3, y: 3 },
    lg: { x: 3, y: 3 },
    xl: { x: 3, y: 3 },
};

const loaderPositionStyles = css<UploadInputDisplayRootProps>(
    ({ theme: { spacing }, size }) => css`
        --loader-top-x: ${spacing[LOADER_POSITIONS[size].x]};
        --loader-top-y: ${spacing[LOADER_POSITIONS[size].y]};

        &.full-width.xs {
            --loader-top-x: ${spacing["3"]};
        }

        //Center the loader for small sizes
        &.xs:not(.full-width),
        &.sm:not(.full-width),
        &.md:not(.full-width) {
            --loader-top-x: 50%;
            --loader-top-y: 50%;
            .upload-loading {
                transform: translate(50%, -50%);
            }
        }

        //Position the loader
        .upload-loading {
            position: absolute;
            top: var(--loader-top-y);
            right: var(--loader-top-x);
            z-index: 2;
        }
    `,
);

const shadowStyles = css(
    ({ theme: { palette, borderRadius } }) => css`
        --shadow-color: transparent;
        --opacity: 0;

        &.isLoading {
            --shadow-color: ${palette.overlays[800][20]};
            --opacity: 1;
        }
        &.isSuccess:not(.isLoading) {
            --shadow-color: ${alpha(palette.status.success, 0.4)};
            --opacity: 1;
        }
        &.isError:not(.isLoading) {
            --shadow-color: ${alpha(palette.status.error, 0.4)};
            --opacity: 1;
        }

        //Top blur effect
        &:after {
            content: "";
            position: absolute;
            top: -10%;
            left: 0;
            width: 100%;
            height: 70%;
            border-radius: ${borderRadius} ${borderRadius} 0 0;
            background: linear-gradient(0deg, rgba(255, 255, 255, 0) 40%, var(--shadow-color) 100%);
            transition: all 0.2s;
            z-index: 2;
        }
    `,
);

const skeletonStyles = css(
    () => css`
        .Skeleton {
            position: absolute;
            width: 100%;
            height: 100%;
        }
    `,
);

export const UploadInputDisplayRoot = styled(Col)<UploadInputDisplayRootProps>(
    ({ theme: { palette, borderRadius } }) => css`
        border-radius: ${borderRadius};
        position: relative;
        overflow: hidden;

        //Background color
        transition: background-color 0.4s;
        background-color: ${palette.overlays[800][8]};
        &:hover {
            background-color: ${palette.overlays[800][20]};
        }

        //Disabled
        &:not(.isEditable) {
            background-color: ${palette.disabled};
            &:hover {
                background-color: ${palette.disabled};
            }
        }

        //Loader position based on size
        ${loaderPositionStyles}

        //Shadow styles
        ${shadowStyles}

        //Skeleton styles
        ${skeletonStyles}

        //Size styles
        ${fileUploadSizeStyles}
        &.fullWidth {
            width: 100%;
        }
    `,
);
